import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import {
  fetchBuyerMe,
  startBuyerReserve,
  type BuyerProfile,
  type ReserveHandoff,
} from "@/features/buyerAuth";
import {
  createReservation,
  type PickupPreference,
} from "@/features/storefront";
import { useShopCart } from "./shop";

export const Route = createFileRoute("/shop/reserve")({
  component: ShopReserve,
});

function ShopReserve() {
  const cart = useShopCart();
  const navigate = useNavigate();

  // TNK-202 — guest reserve onboarding. A signed-in buyer reserves directly; a
  // guest first sees an explainer step (register-to-reserve + how it works),
  // then an email form whose submit sends their first magic link carrying the
  // cart. `buyer`: undefined = /buyer/me in flight; null = guest; profile = in.
  const [buyer, setBuyer] = useState<BuyerProfile | null | undefined>(
    undefined,
  );
  const [step, setStep] = useState<"intro" | "form">("intro");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [pickup, setPickup] = useState<PickupPreference>("either");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchBuyerMe().then((b) => {
      setBuyer(b);
      if (b?.name) setName(b.name);
    });
  }, []);

  if (cart.count === 0 && !sent) {
    return (
      <div className="space-y-4 py-12 text-center">
        <p className="text-muted-foreground">Your reservation is empty.</p>
        <Link
          to="/shop/inventory"
          className="text-sm font-medium text-primary hover:underline"
        >
          Browse cards
        </Link>
      </div>
    );
  }

  if (buyer === undefined) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const isGuest = buyer === null;
  const count = cart.count;
  const buildPayload = (): ReserveHandoff => ({
    cardIds: cart.ids,
    productIds: cart.productIds,
    buyerName: name.trim() || undefined,
    buyerNote: note.trim() || undefined,
    pickupPreference: pickup,
    shippingAddress:
      pickup !== "pickup" && address.trim()
        ? { address: address.trim() }
        : undefined,
  });

  // ── Check-your-email (post-submit) — nudge completion ───────────────────────
  if (sent) {
    const resend = async () => {
      setResending(true);
      setResendNote(null);
      try {
        await startBuyerReserve(email.trim(), buildPayload());
        setResendNote("Sent again — check your inbox and spam folder.");
      } catch (e) {
        setResendNote(
          e instanceof Error
            ? e.message
            : "Couldn't resend just now — try again shortly.",
        );
      } finally {
        setResending(false);
      }
    };
    return (
      <div className="mx-auto max-w-md space-y-5 py-10">
        <div className="space-y-2 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-2xl">
            ✉️
          </div>
          <h1 className="shop-display text-2xl font-bold text-foreground">
            Check your email to finish
          </h1>
          <p className="text-sm text-muted-foreground">
            We sent a one-tap sign-in link to{" "}
            <span className="font-medium text-foreground">{email.trim()}</span>.
            Open it within 15 minutes and your reservation is placed
            automatically.
          </p>
        </div>

        <div className="space-y-2 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">
              Your cart is saved.
            </span>{" "}
            The reservation isn’t placed until you open the link, so nothing’s
            lost if you finish later.
          </p>
          <p>Can’t find the email? Check your spam or promotions folder.</p>
        </div>

        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => void resend()}
            disabled={resending}
            className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
          >
            {resending ? "Resending…" : "Resend the link"}
          </button>
          {resendNote && (
            <p className="text-xs text-muted-foreground">{resendNote}</p>
          )}
          <Link
            to="/shop/inventory"
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            Keep browsing
          </Link>
        </div>
      </div>
    );
  }

  // ── Guest onboarding intro — register-to-reserve + how it works ─────────────
  if (isGuest && step === "intro") {
    return (
      <div className="mx-auto max-w-md space-y-5">
        <div className="space-y-2">
          <h1 className="shop-display text-2xl font-bold text-foreground">
            Reserve your cards
          </h1>
          <p className="text-sm text-muted-foreground">
            Reserving {count} item{count !== 1 ? "s" : ""}. A reservation is a
            request the seller confirms — so we tie it to a quick, free account.
            That’s how you track it, get the seller’s offer, and confirm pickup
            or shipping. <span className="text-foreground">No password.</span>
          </p>
        </div>

        <ol className="space-y-3">
          {[
            "Enter your email.",
            "We send a one-tap sign-in link — no password to create.",
            "Open the link and your reservation is placed. Takes about 30 seconds.",
          ].map((text, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {i + 1}
              </span>
              <span className="text-sm text-foreground">{text}</span>
            </li>
          ))}
        </ol>

        <p className="text-xs text-muted-foreground">
          Already have an account? Use the same email — we’ll recognize you and
          sign you straight in.
        </p>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setStep("form")}
            className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Continue
          </button>
          <Link
            to="/shop/cart"
            className="text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Back to cart
          </Link>
        </div>
      </div>
    );
  }

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (isGuest) {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
          setError("Enter your email to reserve.");
          return;
        }
        await startBuyerReserve(trimmedEmail, buildPayload());
        setSent(true);
      } else {
        const { id } = await createReservation({
          ...buildPayload(),
          pickupPreference: pickup,
        });
        cart.clear();
        void navigate({
          to: "/shop/account/reservations/$id",
          params: { id },
        });
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not submit reservation.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-4">
      <h1 className="shop-display text-2xl font-bold text-foreground">
        Reserve your cards
      </h1>
      <p className="text-sm text-muted-foreground">
        Reserving {count} item{count !== 1 ? "s" : ""}. This is a request — the
        seller confirms availability and follows up by email.
      </p>

      <Field label="Email">
        {isGuest ? (
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className={inputClass}
          />
        ) : (
          <input
            type="email"
            value={buyer.email}
            readOnly
            className={`${inputClass} cursor-not-allowed bg-muted text-muted-foreground`}
          />
        )}
      </Field>
      {isGuest && (
        <p className="-mt-2 text-xs text-muted-foreground">
          We’ll email a one-tap sign-in link — your reservation is placed when
          you open it.
        </p>
      )}

      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Optional"
          className={inputClass}
        />
      </Field>

      <Field label="Pickup or shipping">
        <div className="flex flex-wrap gap-2">
          {(
            [
              { v: "either", l: "Either" },
              { v: "pickup", l: "Local pickup" },
              { v: "ship", l: "Ship to me" },
            ] as { v: PickupPreference; l: string }[]
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setPickup(opt.v)}
              className={`rounded-md border px-3 py-1.5 text-sm ${
                pickup === opt.v
                  ? "border-primary bg-primary/10 font-semibold text-foreground"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </Field>

      {pickup !== "pickup" && (
        <Field label="Shipping address">
          <textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Name, street, city, state, ZIP"
            rows={3}
            className={`${inputClass} resize-y`}
          />
        </Field>
      )}

      <Field label="Note to seller">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional"
          rows={2}
          className={`${inputClass} resize-y`}
        />
      </Field>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting}
          className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting
            ? "Submitting…"
            : isGuest
              ? "Reserve & send sign-in link"
              : "Submit reservation"}
        </button>
        {isGuest ? (
          <button
            type="button"
            onClick={() => setStep("intro")}
            className="text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Back
          </button>
        ) : (
          <Link
            to="/shop/cart"
            className="text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Back to cart
          </Link>
        )}
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium tracking-wide text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {children}
    </label>
  );
}
